import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePrefs } from "@/context/PrefsContext";
import { useScreenTour } from "@/hooks/useScreenTour";
import { PageHeader } from "@/components/PageHeader";
import { MargenBadge, EstadoRegistroBadge, EstadoCobroBadge, EstadoPagoBadge, TipoViajeBadge, FueraTarifaBadge, BloqueadoBadge, CompanyChip } from "@/components/LoadBadges";
import { EmptyFiltered } from "@/components/EmptyFiltered";
import { InfoTooltip } from "@/components/InfoTooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, ArrowUpDown, Download, FileSpreadsheet, Lock, FileUp } from "lucide-react";

const money = (n, currency = "USD") => (n == null ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`);

// P41 (2026-10-02): el cliente pidió ver SU cliente (como figura en QuickBooks o en DAT) y no el nombre
// corto del catálogo. Si son distintos, el del catálogo queda debajo, en gris.
const normNombre = (s) => (s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
function ClienteCelda({ load }) {
  const fuente = load.cliente_fuente?.nombre;
  const distinto = fuente && normNombre(fuente) !== normNombre(load.client_nombre);
  return (
    <span title={fuente ? `Así figura en ${load.cliente_fuente.sistema}. En la plataforma: ${load.client_nombre || "—"}` : undefined}>
      <span className="block truncate">{fuente || load.client_nombre}</span>
      {distinto && <span className="block truncate text-[11px] text-muted-foreground" data-testid={`load-row-${load.numero_load}-cliente-plataforma`}>{load.client_nombre}</span>}
    </span>
  );
}

const EMPTY_FILTERS = {
  company_id: "all", client_id: "", carrier_id: "", tipo_viaje: "all", estado_cobro: "all",
  estado_pago_carrier: "all", estado_registro: "activo", fecha_desde: "", fecha_hasta: "", search: "",
  fuera_de_tarifa: "all", unidad: "", sin_fecha_delivery: "all",
};

export default function Loads() {
  const { can } = useAuth();
  const { companyFilter, setCompanyFilter } = usePrefs();
  const navigate = useNavigate();
  useScreenTour("loads");
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => ({
    ...EMPTY_FILTERS,
    sin_fecha_delivery: searchParams.get("sin_fecha_delivery") === "true" ? "true" : "all",
    tipo_viaje: searchParams.get("tipo_viaje") || "all",
  }));
  const [companies, setCompanies] = useState([]);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const limit = 20;
  const canVerGanancia = can("loads", "ver_ganancia");

  useEffect(() => {
    api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {});
  }, []);

  // El selector global de compañía (header) es la fuente de verdad — este filtro local se
  // mantiene sincronizado con él en ambas direcciones (Parte D).
  useEffect(() => { setFilters((f) => ({ ...f, company_id: companyFilter })); }, [companyFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort_by: sortBy, sort_dir: sortDir, skip: page * limit, limit };
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      const { data } = await api.get("/loads", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filters, sortBy, sortDir, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const setFilter = (k, v) => {
    setPage(0);
    setFilters((f) => ({ ...f, [k]: v }));
    if (k === "company_id") setCompanyFilter(v);
  };
  const clearFilters = () => { setFilters(EMPTY_FILTERS); setCompanyFilter("all"); };
  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortDir("desc"); }
  };

  const exportFile = async (fmt) => {
    try {
      const params = { sort_by: sortBy, sort_dir: sortDir };
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      const res = await api.get(`/loads/export/${fmt}`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `loads.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error("No se pudo exportar");
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / limit));

  const SortHead = ({ field, children, ...rest }) => (
    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort(field)} data-testid={`loads-sort-${field}`} {...rest}>
      <span className="inline-flex items-center gap-1">{children} <ArrowUpDown className="h-3 w-3 text-muted-foreground" /></span>
    </TableHead>
  );

  return (
    <div>
      <PageHeader
        title="Viajes (Loads)"
        subtitle="Todos tus viajes: nacionales (TN), Álvarez (AL) y Trébol USA (T). Aquí vive la facturación."
        actions={
          <div className="flex gap-2" data-tour="loads-export">
            <Button variant="outline" size="sm" onClick={() => exportFile("csv")} className="gap-1" data-testid="loads-export-csv">
              <Download className="h-4 w-4" /> CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportFile("xlsx")} className="gap-1" data-testid="loads-export-xlsx">
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </Button>
            {can("loads", "crear") && (
              <>
                <Button variant="outline" onClick={() => navigate("/documents/nuevo-load")} className="gap-2" data-testid="new-load-from-doc-btn">
                  <FileUp className="h-4 w-4" /> Desde documento
                </Button>
                <Button onClick={() => navigate("/loads/nuevo")} className="gap-2" data-testid="new-load-btn" data-tour="loads-new">
                  <Plus className="h-4 w-4" /> Nuevo load
                </Button>
              </>
            )}
          </div>
        }
      />
      <p className="mb-4 -mt-3 text-xs text-muted-foreground" data-testid="loads-serie-leyenda">
        AL = Álvarez · T = Trébol USA · TN = Trébol Nacional
      </p>

      <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <div className="relative col-span-1 sm:col-span-3 lg:col-span-2" data-tour="loads-search">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Número, referencia o factura" value={filters.search} onChange={(e) => setFilter("search", e.target.value)} className="pl-9" data-testid="loads-search" />
        </div>
        <div className="contents" data-tour="loads-filtros-compania-tipo">
          <Select value={filters.company_id} onValueChange={(v) => setFilter("company_id", v)}>
            <SelectTrigger data-testid="loads-filter-company"><SelectValue placeholder="Compañía" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las compañías</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.tipo_viaje} onValueChange={(v) => setFilter("tipo_viaje", v)}>
            <SelectTrigger data-testid="loads-filter-tipo-viaje"><SelectValue placeholder="Tipo de viaje" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="nacional_mx">Nacional MX</SelectItem>
              <SelectItem value="nacional_us">Nacional US</SelectItem>
              <SelectItem value="transfronterizo">Transfronterizo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Select value={filters.estado_cobro} onValueChange={(v) => setFilter("estado_cobro", v)}>
          <SelectTrigger data-testid="loads-filter-estado-cobro"><SelectValue placeholder="Estado de cobro" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Cobro: todos</SelectItem>
            <SelectItem value="pendiente">Pendiente</SelectItem>
            <SelectItem value="cobrado">Cobrado</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.estado_pago_carrier} onValueChange={(v) => setFilter("estado_pago_carrier", v)}>
          <SelectTrigger data-testid="loads-filter-estado-pago"><SelectValue placeholder="Estado de pago" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Pago: todos</SelectItem>
            <SelectItem value="sin_pagos">Sin pagos</SelectItem>
            <SelectItem value="parcial">Parcial</SelectItem>
            <SelectItem value="pagado_total">Pagado total</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={filters.fecha_desde} onChange={(e) => setFilter("fecha_desde", e.target.value)} data-testid="loads-filter-fecha-desde" />
        <Input type="date" value={filters.fecha_hasta} onChange={(e) => setFilter("fecha_hasta", e.target.value)} data-testid="loads-filter-fecha-hasta" />
        <Select value={filters.estado_registro} onValueChange={(v) => setFilter("estado_registro", v)}>
          <SelectTrigger data-testid="loads-filter-estado-registro"><SelectValue placeholder="Estado del registro" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="activo">Solo activos</SelectItem>
            <SelectItem value="invalidado">Solo invalidados</SelectItem>
            <SelectItem value="all">Todos (activos e invalidados)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.fuera_de_tarifa} onValueChange={(v) => setFilter("fuera_de_tarifa", v)}>
          <SelectTrigger data-testid="loads-filter-fuera-tarifa"><SelectValue placeholder="Tarifa" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tarifa: todos</SelectItem>
            <SelectItem value="true">Solo fuera de tarifa</SelectItem>
            <SelectItem value="false">Solo dentro de tarifa</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Unidad o placas" value={filters.unidad} onChange={(e) => setFilter("unidad", e.target.value)} data-testid="loads-filter-unidad" />
        <Select value={filters.sin_fecha_delivery} onValueChange={(v) => setFilter("sin_fecha_delivery", v)}>
          <SelectTrigger data-testid="loads-filter-sin-fecha-delivery"><SelectValue placeholder="Fecha de entrega" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Fecha de entrega: todos</SelectItem>
            <SelectItem value="true">Sin fecha de entrega (estimado)</SelectItem>
            <SelectItem value="false">Con fecha de entrega</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-md border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <SortHead field="numero_load" data-tour="loads-numero-column">Número</SortHead>
              <TableHead>Compañía</TableHead>
              <TableHead>Cliente</TableHead>
              <SortHead field="referencia_cliente">Referencia</SortHead>
              <TableHead>Ruta</TableHead>
              <SortHead field="fecha_pickup">Pickup</SortHead>
              <TableHead>Unidad</TableHead>
              <TableHead>Placas</TableHead>
              <TableHead>Operador</TableHead>
              <SortHead field="monto_cobro_usd">Cobro</SortHead>
              <TableHead>Costos</TableHead>
              {canVerGanancia && <SortHead field="ganancia_neta_usd">Ganancia</SortHead>}
              {canVerGanancia && <SortHead field="margen_pct">Margen</SortHead>}
              <TableHead data-tour="loads-cobro-column">Cobro</TableHead>
              <TableHead>Pago</TableHead>
              <TableHead>Estado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((l) => (
              <TableRow
                key={l.id}
                data-testid={`load-row-${l.numero_load}`}
                className={`cursor-pointer hover:bg-slate-50 ${l.estado_registro === "invalidado" ? "opacity-60 line-through" : ""}`}
                onClick={() => navigate(`/loads/${l.id}`)}
              >
                <TableCell className="font-mono text-xs font-semibold">
                  <span className="inline-flex items-center gap-1">
                    {l.bloqueado && <Lock className="h-3 w-3 text-slate-500" data-testid={`load-row-${l.numero_load}-lock-icon`} />}
                    {l.numero_load} {l.tipo_viaje === "transfronterizo" && <TipoViajeBadge tipo={l.tipo_viaje} testId={`load-row-${l.numero_load}-tipo`} />}
                    {l.fuera_de_tarifa && <FueraTarifaBadge testId={`load-row-${l.numero_load}-fuera-tarifa`} />}
                  </span>
                  {/* P46: el número de load de tráfico, si es otro que el del viaje */}
                  {(l.loads_trafico || []).some((n) => n !== l.numero_load) && (
                    <span className="block text-[10px] font-normal text-muted-foreground" data-testid={`load-row-${l.numero_load}-trafico`}>
                      tráfico {(l.loads_trafico || []).filter((n) => n !== l.numero_load).join(", ")}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-xs"><CompanyChip numeroLoad={l.numero_load} testId={`load-row-${l.numero_load}-company`} /></TableCell>
                <TableCell className="max-w-[170px] text-sm" data-testid={`load-row-${l.numero_load}-cliente`}><ClienteCelda load={l} /></TableCell>
                <TableCell className="text-xs">{l.referencia_cliente}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{l.origen_ciudad} → {l.destino_ciudad}</TableCell>
                <TableCell className="font-mono text-xs">{l.fecha_pickup}</TableCell>
                <TableCell className="text-xs">{l.unidad || "—"}</TableCell>
                <TableCell className="whitespace-nowrap font-mono text-xs" data-testid={`load-row-${l.numero_load}-placas`}>{l.placas || "—"}</TableCell>
                <TableCell className="text-xs">{l.operador || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{money(l.monto_cobro, l.moneda_cobro)}</TableCell>
                <TableCell className="font-mono text-xs">{canVerGanancia ? money(l.costos_total_usd) : "—"}</TableCell>
                {canVerGanancia && <TableCell className="font-mono text-xs">{money(l.ganancia_neta_usd)}</TableCell>}
                {canVerGanancia && <TableCell><MargenBadge margen_pct={l.margen_pct} margen_color={l.margen_color} testId={`load-row-${l.numero_load}-margen`} /></TableCell>}
                <TableCell>
                  <EstadoCobroBadge estado={l.estado_cobro_semaforo} dias={l.dias_vencimiento_cobro} testId={`load-row-${l.numero_load}-cobro`} />
                  {l.fecha_delivery_estimada && (
                    <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-amber-600" data-testid={`load-row-${l.numero_load}-fecha-estimada`}>
                      (vencim. estimado)
                      <InfoTooltip text="Este viaje no tenía fecha de entrega en el Excel, así que la plataforma calculó una fecha estimada desde el despacho." testId={`load-row-${l.numero_load}-fecha-estimada-info`} />
                    </span>
                  )}
                </TableCell>
                <TableCell><EstadoPagoBadge estado={l.estado_pago_carrier} testId={`load-row-${l.numero_load}-pago`} /></TableCell>
                <TableCell><EstadoRegistroBadge estado={l.estado_registro} /></TableCell>
              </TableRow>
            ))}
            {!loading && data.items.length === 0 && (
              <EmptyFiltered colSpan={16} onClear={clearFilters} testId="loads-empty" />
            )}
          </TableBody>
        </Table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
        <span data-testid="loads-total-count">{data.total} load(s)</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
          <span>Pág. {page + 1} de {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
        </div>
      </div>
    </div>
  );
}
